// notificationService/supabase.js - Supabase 适配器

import { supabase } from '../supabase/client.js';

// 🔧 【性能优化】通知缓存机制
const notificationsCache = new Map();
const CACHE_DURATION = 30000; // 30秒缓存

/**
 * Supabase 通知数据适配器
 * 作为通知服务的主要数据源
 */
export const supabaseAdapter = {
  /**
   * 创建通知
   * @param {Object} notification - 通知数据
   * @returns {Promise<Object>} 创建结果
   */
  createNotification: async notification => {
    try {
      // 📥 收到notification参数
      console.log(
        '[supabaseAdapter.createNotification] 📥 收到notification参数:'
      );
      console.log(
        '  - notification.senderId:',
        notification.senderId,
        '(type:',
        typeof notification.senderId,
        ')'
      );
      console.log(
        '  - notification.senderName:',
        notification.senderName,
        '(type:',
        typeof notification.senderName,
        ')'
      );
      console.log(
        '  - notification.senderAvatar:',
        notification.senderAvatar,
        '(type:',
        typeof notification.senderAvatar,
        ')'
      );
      console.log(
        '  - notification.receiverId:',
        notification.receiverId,
        '(type:',
        typeof notification.receiverId,
        ')'
      );
      console.log('  - notification.meta:', notification.meta);
      console.log('  - typeof notification.meta:', typeof notification.meta);
      console.log(
        '  - notification.meta?.applicantId:',
        notification.meta?.applicantId,
        '(type:',
        typeof notification.meta?.applicantId,
        ')'
      );
      console.log(
        '  - notification.meta?.applicantName:',
        notification.meta?.applicantName,
        '(type:',
        typeof notification.meta?.applicantName,
        ')'
      );
      console.log(
        '  - 完整notification对象:',
        JSON.stringify(notification, null, 2)
      );

      // 从多个来源提取 sender_id，确保永远不会是 null
      // 优先级：notification.senderId > meta.applicantId > null (但至少要有 fallback)
      let senderId = notification.senderId;
      if (!senderId && notification.meta?.applicantId) {
        senderId = notification.meta.applicantId;
        console.log(
          '[supabaseAdapter] Using applicantId from meta as senderId:',
          senderId
        );
      }

      // 从多个来源提取 sender_name，确保永远不会是 null 或空字符串
      // 优先级：notification.senderName > meta.applicantName > "Unknown User"
      let senderName = notification.senderName;
      if (!senderName || senderName === '' || senderName === 'Anonymous') {
        if (
          notification.meta?.applicantName &&
          notification.meta.applicantName !== '' &&
          notification.meta.applicantName !== 'Anonymous'
        ) {
          senderName = notification.meta.applicantName;
          console.log(
            '[supabaseAdapter] Using applicantName from meta as senderName:',
            senderName
          );
        } else {
          senderName = senderId
            ? `User_${senderId.substring(0, 8)}`
            : 'Unknown User';
          console.warn(
            '[supabaseAdapter] No valid senderName found, using fallback:',
            senderName
          );
        }
      }

      // 确保 meta 字段是有效的对象，包含所有必要信息
      const meta = {
        ...(notification.meta || {}),
        // 确保 meta 中包含 applicantId 和 applicantName（即使已经保存在 sender_id/sender_name）
        applicantId: senderId || notification.meta?.applicantId || null,
        applicantName:
          senderName || notification.meta?.applicantName || 'Unknown User',
        applicantAvatar:
          notification.meta?.applicantAvatar ||
          notification.senderAvatar ||
          '/assets/placeholder.svg',
      };

      // 转换数据格式以匹配数据库表结构
      const notificationData = {
        type: notification.type,
        sender_id: senderId, // 现在确保不会是 null（如果 senderId 是 null，至少 meta.applicantId 会填充）
        sender_name: senderName, // 现在确保不会是 null 或空字符串
        receiver_id: notification.receiverId,
        title: notification.title,
        content: notification.content,
        message: notification.message,
        is_read: notification.isRead || false,
        created_at: notification.createdAt || new Date().toISOString(),
        meta: meta, // 确保 meta 包含完整信息
      };

      // 💾 准备保存到Supabase的数据
      console.log(
        '[supabaseAdapter.createNotification] 💾 准备保存到Supabase的数据:'
      );
      console.log(
        '  - sender_id:',
        notificationData.sender_id,
        '(type:',
        typeof notificationData.sender_id,
        ')'
      );
      console.log(
        '  - sender_name:',
        notificationData.sender_name,
        '(type:',
        typeof notificationData.sender_name,
        ')'
      );
      console.log(
        '  - receiver_id:',
        notificationData.receiver_id,
        '(type:',
        typeof notificationData.receiver_id,
        ')'
      );
      console.log('  - meta:', notificationData.meta);
      console.log(
        '  - meta.applicantId:',
        notificationData.meta.applicantId,
        '(type:',
        typeof notificationData.meta.applicantId,
        ')'
      );
      console.log(
        '  - meta.applicantName:',
        notificationData.meta.applicantName,
        '(type:',
        typeof notificationData.meta.applicantName,
        ')'
      );
      console.log('  - 完整对象:', JSON.stringify(notificationData, null, 2));

      // ❌ 如果关键字段是null，立即报错
      if (notificationData.sender_id === null) {
        console.error('❌❌❌ CRITICAL: sender_id是null，无法保存！');
        console.error('原始notification.senderId:', notification.senderId);
        console.error(
          '原始notification.meta?.applicantId:',
          notification.meta?.applicantId
        );
      }
      if (
        notificationData.sender_name === 'Unknown User' ||
        notificationData.sender_name === 'Anonymous'
      ) {
        console.error(
          '❌❌❌ CRITICAL: sender_name无效:',
          notificationData.sender_name
        );
        console.error('原始notification.senderName:', notification.senderName);
        console.error(
          '原始notification.meta?.applicantName:',
          notification.meta?.applicantName
        );
      }

      const { data, error } = await supabase
        .from('notifications')
        .insert(notificationData)
        .select()
        .single();

      if (error) {
        console.error('[supabaseAdapter] Error creating notification:', error);
        console.error(
          '[supabaseAdapter] Failed notification data:',
          notificationData
        );
        return { success: false, error: error.message };
      }

      console.log(
        '[supabaseAdapter] Successfully created notification in Supabase:',
        data.id,
        'with sender_id:',
        data.sender_id,
        'sender_name:',
        data.sender_name
      );

      // 🔧 【性能优化】清除相关缓存
      if (notificationData?.receiver_id) {
        const cacheKeys = Array.from(notificationsCache.keys()).filter(key =>
          key.includes(`_${notificationData.receiver_id}_`)
        );
        cacheKeys.forEach(key => notificationsCache.delete(key));
        if (cacheKeys.length > 0) {
          console.log(
            `[supabaseAdapter] Cleared ${cacheKeys.length} notification cache entries after create`
          );
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error('[supabaseAdapter] Error creating notification:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 获取用户通知列表
   * @param {string} userId - 用户ID
   * @param {Object} options - 查询选项
   * @param {number} options.limit - 限制数量
   * @param {number} options.offset - 偏移量
   * @param {string} options.type - 通知类型过滤
   * @returns {Promise<Object>} 通知列表
   */
  getUserNotifications: async (userId, options = {}) => {
    try {
      const { limit = 50, offset = 0, type = null } = options;

      // 🔧 【性能优化】检查缓存
      const cacheKey = `notifications_${userId}_${limit}_${offset}_${
        type || 'all'
      }`;
      const cached = notificationsCache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(
          `[supabaseAdapter] ✅ Using cached notifications for user ${userId} (${cached.data.length} items)`
        );
        return { success: true, data: cached.data };
      }

      console.log(
        `[supabaseAdapter] 🔄 Fetching fresh notifications for user ${userId}...`
      );
      console.time(`[supabaseAdapter] Notification Query Time (${userId})`);

      let query = supabase
        .from('notifications')
        .select('*')
        .eq('receiver_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // 如果指定了类型过滤
      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query;

      console.timeEnd(`[supabaseAdapter] Notification Query Time (${userId})`);

      if (error) {
        console.error('[supabaseAdapter] Error getting notifications:', error);
        return { success: false, error: error.message };
      }

      // 转换数据格式以匹配前端期望的格式
      const notifications = (data || []).map(notification => {
        // 确保 meta 字段正确解析（可能是 JSON 字符串）
        let meta = notification.meta || {};
        if (typeof meta === 'string') {
          try {
            meta = JSON.parse(meta);
          } catch (e) {
            console.warn('[supabaseAdapter] Failed to parse meta as JSON:', e);
            meta = {};
          }
        }

        return {
          id: notification.id,
          type: notification.type,
          senderId: notification.sender_id,
          senderName: notification.sender_name,
          receiverId: notification.receiver_id,
          title: notification.title,
          content: notification.content,
          message: notification.message,
          isRead: notification.is_read,
          createdAt: notification.created_at,
          updatedAt: notification.updated_at,
          meta: meta,
        };
      });

      // 🔧 【性能优化】缓存结果
      notificationsCache.set(cacheKey, {
        data: notifications,
        timestamp: Date.now(),
      });

      console.log(
        `[supabaseAdapter] ✅ Retrieved ${notifications.length} notifications for user ${userId} (cached)`
      );
      return { success: true, data: notifications };
    } catch (error) {
      console.error('[supabaseAdapter] Error getting notifications:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 标记通知为已读
   * @param {string} notificationId - 通知ID
   * @returns {Promise<Object>} 更新结果
   */
  markAsRead: async notificationId => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) {
        console.error(
          '[supabaseAdapter] Error marking notification as read:',
          error
        );
        return { success: false, error: error.message };
      }

      console.log(
        '[supabaseAdapter] Marked notification as read:',
        notificationId
      );

      // 🔧 【性能优化】清除相关缓存
      if (data?.receiver_id) {
        const cacheKeys = Array.from(notificationsCache.keys()).filter(key =>
          key.includes(`_${data.receiver_id}_`)
        );
        cacheKeys.forEach(key => notificationsCache.delete(key));
        if (cacheKeys.length > 0) {
          console.log(
            `[supabaseAdapter] Cleared ${cacheKeys.length} notification cache entries`
          );
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error(
        '[supabaseAdapter] Error marking notification as read:',
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 标记用户所有通知为已读
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 更新结果
   */
  markAllAsRead: async userId => {
    try {
      // 直接使用 UPDATE 而不是 RPC（因为 RPC 函数可能不存在）
      const { data, error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          updated_at: new Date().toISOString(),
        })
        .eq('receiver_id', userId)
        .eq('is_read', false)
        .select();

      if (error) {
        console.error(
          '[supabaseAdapter] Error marking all notifications as read:',
          error
        );
        return { success: false, error: error.message };
      }

      console.log(
        `[supabaseAdapter] Marked ${data.length} notifications as read for user ${userId}`
      );

      // 🔧 【性能优化】清除相关缓存
      const cacheKeys = Array.from(notificationsCache.keys()).filter(key =>
        key.includes(`_${userId}_`)
      );
      cacheKeys.forEach(key => notificationsCache.delete(key));
      if (cacheKeys.length > 0) {
        console.log(
          `[supabaseAdapter] Cleared ${cacheKeys.length} notification cache entries`
        );
      }

      return { success: true, data: { updatedCount: data.length } };
    } catch (error) {
      console.error(
        '[supabaseAdapter] Error marking all notifications as read:',
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 删除通知
   * @param {string} notificationId - 通知ID
   * @returns {Promise<Object>} 删除结果
   */
  deleteNotification: async notificationId => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('[supabaseAdapter] Error deleting notification:', error);
        return { success: false, error: error.message };
      }

      console.log('[supabaseAdapter] Deleted notification:', notificationId);
      return { success: true };
    } catch (error) {
      console.error('[supabaseAdapter] Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 获取用户未读通知数量
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 未读数量
   */
  getUnreadCount: async userId => {
    try {
      // 直接查询而不是使用 RPC
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('[supabaseAdapter] Error getting unread count:', error);
        return { success: false, error: error.message };
      }

      console.log(`[supabaseAdapter] Unread count for ${userId}: ${count}`);
      return { success: true, data: count || 0 };
    } catch (error) {
      console.error('[supabaseAdapter] Error getting unread count:', error);
      return { success: false, error: error.message };
    }
  },
};
